import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import EtikettenDruckDialog from './EtikettenDruckDialog';
import { fetchApi } from './fetchApi';

type ArtikelOption = {
  id: number;
  sku: string;
  bezeichnung: string;
};

interface Kennzahlen {
  gesamtbestand: number;
  gesamtlagerwert: number;
  offene_lieferscheine: number;
  unter_mindestbestand: number;
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  wert: string;
  onClick?: () => void;
}

function KpiCard({ icon, label, wert, onClick }: KpiCardProps) {
  return (
    <Card
      sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {wert}
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.55, mt: 1, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.8px' }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ ml: 2, mt: 0.5 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [kennzahlen, setKennzahlen] = useState<Kennzahlen | null>(null);
  const [kennzahlenFehler, setKennzahlenFehler] = useState<string | null>(null);
  const [kennzahlenLaedt, setKennzahlenLaedt] = useState(true);

  const [etikettenOpen, setEtikettenOpen] = useState(false);
  const [artikelOptionen, setArtikelOptionen] = useState<ArtikelOption[]>([]);
  const [artikelLaden, setArtikelLaden] = useState(false);
  const [artikelFehler, setArtikelFehler] = useState<string | null>(null);

  const schnellzugriff = [
    { label: 'Artikel erfassen', pfad: '/artikel' },
    { label: 'Artikel ohne Bestand', pfad: '/artikel-ohne-bestand' },
    { label: 'Leere Boxen', pfad: '/leere-boxen' },
    { label: 'CSV-Import', pfad: '/import' },
  ];

  useEffect(() => {
    fetchApi('/api/view/dashboard')
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((data: Kennzahlen) => setKennzahlen(data))
      .catch((err: any) => {
        setKennzahlenFehler(
          err?.message === '401'
            ? 'Session abgelaufen. Bitte neu einloggen.'
            : 'Kennzahlen konnten nicht geladen werden.'
        );
      })
      .finally(() => setKennzahlenLaedt(false));

    ladeArtikelOptionen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ladeArtikelOptionen = async () => {
    setArtikelFehler(null);
    setArtikelLaden(true);
    try {
      const resp = await fetchApi('/api/artikel');
      const data = await resp.json();
      const opts: ArtikelOption[] = (Array.isArray(data) ? data : [])
        .map((a: any) => ({
          id: Number(a.id),
          sku: String(a.sku ?? ''),
          bezeichnung: String(a.bezeichnung ?? ''),
        }))
        .filter((a: ArtikelOption) => Number.isFinite(a.id) && a.id > 0 && a.sku.length > 0);
      setArtikelOptionen(opts);
    } catch (e: any) {
      if (e?.message === '401') {
        setArtikelFehler('Session abgelaufen. Bitte neu einloggen.');
      } else {
        setArtikelFehler(e?.message || 'Fehler beim Laden der Artikel.');
      }
      setArtikelOptionen([]);
    } finally {
      setArtikelLaden(false);
    }
  };

  const handleOpenEtiketten = async () => {
    if (!artikelOptionen.length && !artikelLaden) {
      await ladeArtikelOptionen();
    }
    setEtikettenOpen(true);
  };

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.55, mt: 0.5 }}>
          Lagerübersicht auf einen Blick
        </Typography>
      </Box>

      {/* KPI Cards */}
      {kennzahlenFehler && (
        <Alert severity="error" sx={{ mb: 3 }}>{kennzahlenFehler}</Alert>
      )}

      {kennzahlenLaedt ? (
        <Box display="flex" justifyContent="center" mt={4} mb={4}>
          <CircularProgress />
        </Box>
      ) : kennzahlen && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<Inventory2Icon sx={{ fontSize: 38, color: '#e8820e' }} />}
              label="Gesamtbestand"
              wert={Number(kennzahlen.gesamtbestand).toLocaleString('de-AT')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<EuroSymbolIcon sx={{ fontSize: 38, color: '#e8820e' }} />}
              label="Gesamtlagerwert (EK)"
              wert={formatEuro(Number(kennzahlen.gesamtlagerwert))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<LocalShippingIcon sx={{ fontSize: 38, color: '#e8820e' }} />}
              label="Offene Lieferscheine"
              wert={String(kennzahlen.offene_lieferscheine)}
              onClick={() => navigate('/lieferschein-liste')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              icon={<WarningAmberIcon sx={{ fontSize: 38, color: '#e8820e' }} />}
              label="Unter Mindestbestand"
              wert={String(kennzahlen.unter_mindestbestand)}
              onClick={() => navigate('/mindestbestand')}
            />
          </Grid>
        </Grid>
      )}

      <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

      {/* Schnellzugriff */}
      <Box mb={2}>
        <Typography variant="overline" sx={{ opacity: 0.55, letterSpacing: '1px' }}>
          Schnellzugriff
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {schnellzugriff.map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.pfad}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(s.pfad)}
              sx={{ py: 1.2 }}
            >
              {s.label}
            </Button>
          </Grid>
        ))}

        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleOpenEtiketten}
            disabled={artikelLaden}
            sx={{ py: 1.2 }}
          >
            {artikelLaden ? 'Lade Artikel…' : 'Barcode-Etiketten drucken'}
          </Button>
          {artikelFehler && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {artikelFehler}
            </Typography>
          )}
        </Grid>
      </Grid>

      <EtikettenDruckDialog
        open={etikettenOpen}
        onClose={() => setEtikettenOpen(false)}
        artikelOptionen={artikelOptionen}
      />
    </Container>
  );
}
